import { Edit } from '@mui/icons-material';
import { CustomAccordion, CustomAccordionSummary } from './PatientProfileInfoAccordion.component';
import { AccordionDetails, Box, Chip, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Formik, FormikConfig, FormikHelpers, FormikProps, FormikValues } from 'formik';
import { Button, Loader, Modal, ModalActions, ModalContent, useAlert } from '../../../../..';
import { useL10n } from '@uplift/hooks';

export type PatientProfileInfoAccordionEditableProps<Values> = {
  children?: React.ReactNode | ((onEdit: () => void) => React.ReactNode);
  editable?: boolean;
  title: string;
  formikConfig: FormikConfig<Values>;
  form: React.ReactNode;
  loading?: boolean;
  inlineForm?: boolean;
  onSuccess?: (onCancelEdit: () => void) => Promise<void> | void;
  onError?: (error: any, onCancelEdit: () => void) => Promise<void> | void;
  hideEditIcon?: boolean;
};

const useStyles = makeStyles()({
  paperWidthMd: {
    maxWidth: '600px'
  }
});

export const PatientProfileInfoAccordionEditable = <Values extends FormikValues>({ editable = true, inlineForm = false, hideEditIcon = false, ...props }: PatientProfileInfoAccordionEditableProps<Values>) => {
  const l10n = useL10n();
  const alert = useAlert();
  const { classes } = useStyles();
  const [ isEditing, setIsEditing ] = useState(false);

  const onEdit = () => setIsEditing(true);
  const onCancelEdit = () => setIsEditing(false);

  // A dumb hack to override the 180 degree flip for icons in mui accordian
  const editIcon = editable && !hideEditIcon && !isEditing && !props.loading
    ? (
      <IconButton onClick={onEdit} size="small">
        <Edit fontSize="small" color="secondary" style={{ 'transform': 'rotate(180deg)' }}/>
      </IconButton>
    ) : null;

  const onSubmit = useCallback(async (values: Values, formikHelpers: FormikHelpers<Values>) => {
    try {
      await props.formikConfig.onSubmit(values, formikHelpers);

      if (props.onSuccess) {
        await props.onSuccess(onCancelEdit);
      } else {
        alert.success('Profile updated');
        onCancelEdit();
      }
    } catch (error) {
      if (props.onError) {
        await props.onError(error, onCancelEdit);
      } else {
        alert.error(`There was a problem updating this profile. Try again or contact ${l10n.helpEmail}`);
      }
    }
  }, [ alert, l10n.helpEmail, props ]);

  const renderButtons = useCallback((formik: FormikProps<Values>) => {
    return (
      <Fragment>
        <Button
          onMouseDown={onCancelEdit}
          onClick={onCancelEdit}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.dirty}
          loading={formik.isSubmitting}
        >
          Save
        </Button>
      </Fragment>
    );
  }, []);

  const content = useMemo(() => {
    if (!isEditing || !inlineForm) {
      return typeof props.children === 'function' ? props.children(onEdit) : props.children;
    }

    return (
      <Formik enableReinitialize {...props.formikConfig} onSubmit={onSubmit}>
        {(formik) => (
          <Box display="flex" flexDirection="column" gap="2.5rem" width="100%">
            <div>{props.form}</div>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap="0.5rem">
              {renderButtons(formik)}
            </Box>
          </Box>
        )}
      </Formik>
    );
  }, [ inlineForm, isEditing, onSubmit, props, renderButtons ]);

  return (
    <>
      <CustomAccordion
        elevation={0}
        expanded
      >
        <CustomAccordionSummary
          style={{ cursor: 'default' }}
          expandIcon={editIcon}
        >
          <Typography style={{ fontSize: '18px' }}>
            {props.title}
          </Typography>
          {props.loading && <Chip size="small" label={<Loader width="24px" size="12px" thickness={6} color="inherit" />} />}
        </CustomAccordionSummary>
        {!props.loading && (
          <AccordionDetails>
            {content}
          </AccordionDetails>
        )}
      </CustomAccordion>

      <Modal
        open={isEditing && !inlineForm}
        onClose={onCancelEdit}
        maxWidth="md"
        fullWidth
        showCloseIcon
        title={`Edit ${props.title}`}
        classes={{ paperWidthMd: classes.paperWidthMd }}
      >
        <Formik enableReinitialize {...props.formikConfig} onSubmit={onSubmit}>
          {(formik) => (
            <>
              <ModalContent>
                <div>{props.form}</div>
              </ModalContent>
              <ModalActions>
                {renderButtons(formik)}
              </ModalActions>
            </>
          )}
        </Formik>
      </Modal>
    </>
  );
};
